// src/features/summary/components/SummaryPage.tsx
//
// «Resumen» (mockup 05) — route /summary, Dirección. Scope + period picker
// with comparison, `KpiHero` tiles (a dashboard, so it's allowed here), the
// rows table with honest placeholders for `available:false` rows, the
// `byDay` stacked bars, the check-in-by-channel table, the `noRecord` line
// and the `estimatedArrivalDates` note. Table-header view switch (count vs
// %) persisted in preferences (mockup callout 8).
import React, { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
  useTheme,
} from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { KpiHero } from '@/components/common/KpiHero';
import { WidgetCard } from '@/components/common/WidgetCard';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import { StackedBarsChart } from '@/components/common/analytics/StackedBarsChart';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { useViewSwitch } from '@/providers/preferencesProvider';
import { hasResourcePermission } from '@/config/resourceConfig';
import { getChannelHex, getChannelShortLabelKey } from '@/design/registry/channelRegistry';
import { hasChainAverage } from '@/lib/summaryView';
import { DASH, formatCount, formatDelta, formatMoney, formatMoneyFigure, formatPct, formatPointsDelta, formatSeconds } from '@/lib/analyticsFormat';
import { arrivalsLabelKey, arrivalsSourceNote } from '@/lib/arrivalsSource';
import type { AnalyticsArrivalsResponse, SummaryRow, SummaryRowKey } from '@/types/analytics';

const ROW_ORDER: SummaryRowKey[] = [
  'arrivals', 'invited', 'preCheckInDone', 'preCheckInPartial',
  'keyWithoutReception', 'airLobby', 'kioskWithoutPreCheckIn', 'noRecord',
];

export const SummaryPage: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();
  const exportCsv = useCsvExport({ page: 'summary' });
  const canExport = hasResourcePermission('summary', 'export');
  const [rowsView, setRowsView] = useViewSwitch('summary', 'rowsView', 'count');
  const dateLocale = translate('common.locale') === 'en' ? enUS : es;

  const [period, setPeriod] = useAnalyticsPeriod();
  const { data, loading, error, refresh } = useAnalyticsFetch<AnalyticsArrivalsResponse>(
    () => (dataProvider as any).getAnalyticsArrivals({ ...scope.scopeParams, from: period.from, to: period.to, compare: period.compare }),
    [dataProvider, scope.scopeParams, period.from, period.to, period.compare]
  );

  // `date` arrives as `2026-08-24`, but the shared date normaliser rewrites it into a full
  // ISO instant on the way in, so the label is built from the parsed day rather than sliced.
  const byDayData = useMemo(
    () =>
      (data?.byDay ?? []).map((d) => {
        const day = new Date(d.date);
        return { ...d, date: Number.isNaN(day.getTime()) ? d.date : format(day, 'd MMM', { locale: dateLocale }) };
      }),
    [data, dateLocale]
  );

  const handleExport = async () => {
    if (!data) return;
    await exportCsv(
      data.rows,
      [
        { labelKey: 'resources.summary.export.row', value: (r: SummaryRow) => rowLabel(r) },
        { labelKey: 'resources.summary.rows.columns.count', value: (r: SummaryRow) => (r.available ? r.count : '') },
        { labelKey: 'resources.summary.rows.columns.pct', value: (r: SummaryRow) => (r.available ? r.pct : '') },
        { labelKey: 'resources.summary.rows.columns.previous', value: (r: SummaryRow) => (r.available ? r.previous : '') },
      ],
      { from: period.from, to: period.to }
    );
  };

  const summaryRows = useMemo(
    () => ROW_ORDER.map((key) => data?.rows.find((r) => r.key === key)).filter((row): row is SummaryRow => Boolean(row)),
    [data]
  );

  const arrivalsLabel = translate(arrivalsLabelKey(data?.arrivalsSource));
  const rowLabel = (row: SummaryRow) => (row.key === 'arrivals' ? arrivalsLabel : translate(`resources.summary.rows.${row.key}`));
  const hotelName = (id: string) => scope.allHotels.find((h) => h.id === id)?.name;
  const sourceNote = data ? arrivalsSourceNote(data.arrivalsSourceByHotel, hotelName) : null;

  const summaryColumns: MetricsColumn<SummaryRow>[] = [
    { key: 'row', label: translate('resources.summary.rows.title'), render: rowLabel },
    {
      key: 'value',
      label: rowsView === 'count' ? translate('resources.summary.rows.columns.count') : translate('resources.summary.rows.columns.pct'),
      align: 'right',
      render: (row) =>
        row.available ? (
          rowsView === 'count' ? formatCount(row.count) : formatPct(row.pct)
        ) : (
          <Typography component="span" variant="caption" color="text.secondary">
            {translate('resources.summary.rows.availableSoon')}
          </Typography>
        ),
    },
    // Unticked «Comparar con el periodo anterior»: no previous-period column at all.
    ...(period.compare
      ? [
          {
            key: 'previous',
            label: translate('resources.summary.rows.columns.previous'),
            align: 'right' as const,
            render: (row: SummaryRow) => (row.available ? (rowsView === 'count' ? formatCount(row.previous) : formatPct(row.previousPct)) : DASH),
          },
        ]
      : []),
    ...(hasChainAverage(data?.rows)
      ? [
          {
            key: 'chainAvg',
            label: translate(rowsView === 'count' ? 'resources.summary.rows.columns.chainAvgShare' : 'resources.summary.rows.columns.chainAvg'),
            align: 'right' as const,
            render: (row: SummaryRow) => (row.available ? formatPct(row.chainAvgPct) : '—'),
          },
        ]
      : []),
  ];

  return (
    <Box>
      <SectionHeader manualScreen="summary"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <PeriodPicker value={period} onChange={setPeriod} />
          </Box>
        }
        subtitle={translate('resources.summary.subtitle')}
        helpConfig={{
          titleKey: 'resources.summary.help.title',
          descriptionKey: 'resources.summary.help.description',
          featureKeys: [
            'resources.summary.help.features.source',
            'resources.summary.help.features.rows',
            'resources.summary.help.features.byDay',
            'resources.summary.help.features.channel',
            'resources.summary.help.features.selfService',
            'resources.summary.help.features.noRecord',
          ],
          tipKeys: ['resources.summary.help.tips.compare'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {error && <FetchErrorBanner onRetry={refresh} />}

      {sourceNote && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {translate(sourceNote.key, sourceNote.params)}
        </Typography>
      )}

      {data && data.estimatedArrivalDates > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {translate('resources.summary.estimatedArrivalDates', { count: data.estimatedArrivalDates })}
        </Typography>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
        <KpiHero
          label={arrivalsLabel}
          value={formatCount(data?.tiles.arrivals)}
          delta={data ? formatDelta(data.tiles.arrivals, data.tiles.arrivalsPrevious) : undefined}
          loading={loading}
        />
        <KpiHero
          label={translate('resources.summary.tiles.resolvedWithoutReception')}
          value={formatPct(data?.tiles.resolvedWithoutReception.pct)}
          sublabel={
            data
              ? translate('resources.summary.tiles.resolvedWithoutReceptionDetail', {
                  count: formatCount(data.tiles.resolvedWithoutReception.count),
                  total: formatCount(data.tiles.arrivals),
                  smart_count: data.tiles.arrivals,
                })
              : undefined
          }
          delta={
            data
              ? formatPointsDelta(data.tiles.resolvedWithoutReception.pct, data.tiles.resolvedWithoutReception.previousPct)
              : undefined
          }
          loading={loading}
        />
        <KpiHero
          label={translate('resources.summary.tiles.chargedSelfService')}
          value={
            data
              ? formatMoneyFigure(data.tiles.chargedSelfService.amount, data.tiles.chargedSelfService.currency, data.tiles.chargedSelfService.byCurrency)
              : formatMoney(undefined)
          }
          sublabel={
            data
              ? translate('resources.summary.tiles.chargedSelfServiceDetail', {
                  count: formatCount(data.tiles.chargedSelfService.reservations),
                  smart_count: data.tiles.chargedSelfService.reservations,
                })
              : undefined
          }
          loading={loading}
        />
      </Box>

      <WidgetCard
        title={translate('resources.summary.rows.title')}
        action={
          <ToggleButtonGroup size="small" exclusive value={rowsView} onChange={(_, v) => v && setRowsView(v)}>
            <ToggleButton value="count" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
              {translate('resources.summary.view.count')}
            </ToggleButton>
            <ToggleButton value="pct" sx={{ textTransform: 'none', fontSize: 12, px: 1.25 }}>
              {translate('resources.summary.view.pct')}
            </ToggleButton>
          </ToggleButtonGroup>
        }
        sx={{ mb: 2.5 }}
      >
        <ResponsiveMetricsTable<SummaryRow>
          columns={summaryColumns}
          rows={summaryRows}
          getRowKey={(row) => row.key}
          loading={loading}
        />
      </WidgetCard>

      <WidgetCard title={translate('resources.summary.byDay.title')} description={translate('resources.summary.byDay.description')} sx={{ mb: 2.5 }}>
        <StackedBarsChart
          data={byDayData}
          xKey="date"
          series={[
            { key: 'withPreCheckIn', labelKey: 'resources.summary.byDay.series.withPreCheckIn', color: getChannelHex('Online') },
            { key: 'kioskWithoutPreCheckIn', labelKey: 'resources.summary.byDay.series.kioskWithoutPreCheckIn', color: getChannelHex('Kiosk') },
            { key: 'airLobby', labelKey: 'resources.summary.byDay.series.airLobby', color: getChannelHex('AirLobby') },
            { key: 'otherCheckIn', labelKey: 'resources.summary.byDay.series.otherCheckIn', color: theme.palette.grey[500] },
            { key: 'noRecord', labelKey: 'resources.summary.byDay.series.noRecord', color: getChannelHex(null) },
            { key: 'pending', labelKey: 'resources.summary.byDay.series.pending', color: theme.palette.action.disabled },
          ]}
        />
      </WidgetCard>

      <Paper sx={{ p: 2.5, mb: 2.5 }}>
        <Typography variant="overline" sx={{ display: 'block', mb: 1.5 }}>
          {translate('resources.summary.checkInByChannel.title')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('resources.summary.checkInByChannel.columns.channel')}</TableCell>
                <TableCell align="right">{translate('resources.summary.checkInByChannel.columns.checkIns')}</TableCell>
                <TableCell align="right">{translate('resources.summary.checkInByChannel.columns.pctOfArrivals')}</TableCell>
                <TableCell align="right">{translate('resources.summary.checkInByChannel.columns.withPreCheckIn')}</TableCell>
                <TableCell align="right">{translate('resources.summary.checkInByChannel.columns.medianSeconds')}</TableCell>
                <TableCell align="right">{translate('resources.summary.checkInByChannel.columns.keysDelivered')}</TableCell>
                <TableCell align="right">{translate('resources.summary.checkInByChannel.columns.charged')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.checkInByChannel ?? []).map((row) => (
                <TableRow key={row.channel}>
                  <TableCell>
                    {row.channel === 'Other' ? translate('resources.summary.checkInByChannel.other') : translate(getChannelShortLabelKey(row.channel))}
                  </TableCell>
                  <TableCell align="right">{formatCount(row.checkIns)}</TableCell>
                  <TableCell align="right">{formatPct(row.pctOfArrivals)}</TableCell>
                  <TableCell align="right">{formatCount(row.withPreCheckIn)}</TableCell>
                  <TableCell align="right">{formatSeconds(row.medianSeconds)}</TableCell>
                  <TableCell align="right">{formatCount(row.keysDelivered)}</TableCell>
                  {/* Money taken on this channel, 0 shown as such; «—» only where no money is
                      taken (the dispenser) or the row has no single channel (Otros). */}
                  <TableCell align="right">{row.channel === 'Dispenser' || row.channel === 'Other' ? DASH : formatMoney(row.charged, row.chargedCurrency ?? 'EUR')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {data && (
        <Typography variant="body2" color="text.secondary">
          {translate('resources.summary.noRecord.line', { count: formatCount(data.noRecord.count), pct: formatPct(data.noRecord.pct) })}
        </Typography>
      )}
    </Box>
  );
};

export default SummaryPage;
